import {
	faBars,
	faCartShopping,
	faClipboardList,
	faComments,
	faFileInvoiceDollar,
	faHouse,
	faStore,
	faUser,
	faXmark,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { PortalMobileNavTab } from '@supply-mover-2-0/shared';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import type { JSX } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDisplaySettings } from '../../hooks/useDisplaySettings';
import { useIsKeyboardLikelyOpen } from '../../hooks/useIsKeyboardLikelyOpen';
import { useCartStore } from '../../stores/cartStore';
import { useChatLauncherStore } from '../../stores/chatLauncherStore';
import { useLoginModalStore } from '../../stores/loginModalStore';
import { useMenuDrawerStore } from '../../stores/menuDrawerStore';
import { usePortalAuthStore } from '../../stores/portalAuthStore';
import { usePortalUnreadCount } from '../PortalNotificationBell';

/**
 * Mobile bottom navigation — Amazon-app pattern.
 *
 * Which tabs render — and their order — is portal-configurable
 * (`portal_site.settings.mobileNavTabs`, PD-14609): the admin picks up
 * to four from the vocabulary below, with Menu always enforced
 * server-side (the drawer is the only path to sign-out / language /
 * theme / long-tail destinations). Tabs whose capability is off for the
 * current visitor are additionally hidden here — e.g. a configured Cart
 * tab never renders on a browse-only storefront, and Chat requires both
 * the portal chat opt-in (PD-14608) and an authenticated session.
 *
 * Auto-hides while the on-screen keyboard is up. Without this, Android's
 * `position: fixed` BottomNav would stay anchored to `innerHeight` while
 * the keyboard shrinks `visualViewport.height`, leaving the nav behind
 * the keyboard and dead-zoning the bottom of the visible area. Same
 * pattern Amazon, Slack, WhatsApp, Twitter, Reddit native apps use.
 */
export function BottomNav(): JSX.Element | null {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const isAuthenticated = usePortalAuthStore((s) => s.isAuthenticated);
	const display = useDisplaySettings();
	const totalQuantity = useCartStore((s) => s.totalQuantity);
	const openLoginModal = useLoginModalStore((s) => s.open);
	const toggleMenuDrawer = useMenuDrawerStore((s) => s.toggle);
	const closeMenuDrawer = useMenuDrawerStore((s) => s.close);
	const isMenuOpen = useMenuDrawerStore((s) => s.isOpen);
	const requestOpenChat = useChatLauncherStore((s) => s.requestOpen);
	const hasActiveChat = useChatLauncherStore((s) => s.hasActiveConversation);
	const chatUnread = useChatLauncherStore((s) => s.unreadCount);
	const keyboardOpen = useIsKeyboardLikelyOpen();
	// PD-16475 — unread notifications live behind the Menu tab on phones (the
	// drawer's Notifications row), so the tab carries a dot while any wait.
	const unreadNotifications = usePortalUnreadCount(isAuthenticated);

	if (keyboardOpen) {
		return null;
	}

	const isHomeActive =
		location.pathname === '/' ||
		location.pathname === '/dashboard' ||
		(location.pathname === '/catalog' &&
			!display.mobileNavTabs.includes('shop')) ||
		(location.pathname.startsWith('/product') &&
			!display.mobileNavTabs.includes('shop'));
	const isShopActive =
		location.pathname === '/catalog' ||
		location.pathname.startsWith('/product');

	const handleProfile = (): void => {
		if (isAuthenticated) {
			void navigate('/account');
		} else {
			openLoginModal();
		}
	};

	// Any non-Menu tab tap dismisses the open drawer before acting —
	// otherwise the navigation happens BEHIND the drawer (the nav is z-50
	// above the drawer's z-40) and the destination stays hidden until the
	// user closes the drawer by hand. Mirrors the X / backdrop behavior.
	const withDrawerClosed = (action: () => void) => (): void => {
		closeMenuDrawer();
		action();
	};

	// Per-tab render definitions. `visible` folds in the capability gates:
	// the config says what the admin WANTS shown; capabilities say what
	// this visitor may actually use.
	const renderTab = (tab: PortalMobileNavTab): JSX.Element | null => {
		switch (tab) {
			case 'home':
				return (
					<TabButton
						key={tab}
						icon={faHouse}
						label={t('Home')}
						active={isHomeActive}
						onClick={withDrawerClosed(() => {
							void navigate('/');
						})}
					/>
				);
			case 'shop':
				if (!display.canBrowseCatalog) {
					return null;
				}
				return (
					<TabButton
						key={tab}
						icon={faStore}
						label={t('Shop')}
						active={isShopActive}
						onClick={withDrawerClosed(() => {
							void navigate('/catalog');
						})}
					/>
				);
			case 'account':
				return (
					<TabButton
						key={tab}
						icon={faUser}
						label={t('Account')}
						active={location.pathname.startsWith('/account')}
						onClick={withDrawerClosed(handleProfile)}
					/>
				);
			case 'cart':
				if (!isAuthenticated || !display.canPurchase) {
					return null;
				}
				return (
					<TabButton
						key={tab}
						icon={faCartShopping}
						label={t('Cart')}
						active={location.pathname === '/cart'}
						onClick={withDrawerClosed(() => {
							void navigate('/cart');
						})}
						badge={totalQuantity > 0 ? totalQuantity : undefined}
					/>
				);
			case 'chat':
				// Chat requires the portal opt-in (PD-14608) AND an
				// authenticated session — the rep router has no client to
				// anchor an anonymous conversation to.
				if (!isAuthenticated || !display.canUseChat) {
					return null;
				}
				return (
					<TabButton
						key={tab}
						icon={faComments}
						label={t('Chat')}
						active={false}
						onClick={withDrawerClosed(requestOpenChat)}
						badge={chatUnread > 0 ? chatUnread : undefined}
						dot={chatUnread === 0 && hasActiveChat}
					/>
				);
			case 'orders':
				if (!isAuthenticated || !display.canViewOrderHistory) {
					return null;
				}
				return (
					<TabButton
						key={tab}
						icon={faClipboardList}
						label={t('Orders')}
						active={location.pathname.startsWith('/orders')}
						onClick={withDrawerClosed(() => {
							void navigate('/orders');
						})}
					/>
				);
			case 'invoices':
				if (!isAuthenticated || !display.canViewInvoices) {
					return null;
				}
				return (
					<TabButton
						key={tab}
						icon={faFileInvoiceDollar}
						label={t('Invoices')}
						active={location.pathname.startsWith('/invoices')}
						onClick={withDrawerClosed(() => {
							void navigate('/invoices');
						})}
					/>
				);
			case 'menu':
				// PD-14705: the Menu tab is a TOGGLE — tapping it while the
				// drawer is open closes it, so open and close share one
				// control at the bottom of the screen (the drawer anchors
				// above this nav, which stays visible and tappable). The icon
				// swaps to an X while open to signal the toggle state.
				return (
					<TabButton
						key={tab}
						icon={isMenuOpen ? faXmark : faBars}
						label={t('Menu')}
						active={isMenuOpen}
						onClick={toggleMenuDrawer}
						dot={!isMenuOpen && unreadNotifications > 0}
					/>
				);
			default:
				return null;
		}
	};

	return (
		<nav
			// z-50 keeps the nav ABOVE the menu drawer (z-40) so the Menu tab
			// stays visible and tappable to toggle the drawer closed (PD-14705).
			// Text and divider follow the primary's readable text color like the
			// header, so a white primary (R&C Trading) gets dark labels and a
			// visible top divider instead of white on white.
			className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-brand-primary text-brand-primary-fg border-t border-brand-primary-fg/10"
			aria-label={t('Primary')}
		>
			{/* Bottom inset: overlap the home-indicator zone by 12px instead of
			    reserving the full env(safe-area-inset-bottom) — the full inset
			    left a fat dead band of brand color under the labels (the tab
			    row already carries its own py). Labels stay clear of the
			    indicator; browsers without an inset are unaffected (max 0). */}
			<div
				className="flex items-stretch"
				style={{
					paddingBottom: 'max(calc(env(safe-area-inset-bottom) - 12px), 0px)',
				}}
			>
				{display.mobileNavTabs.map(renderTab)}
			</div>
		</nav>
	);
}

interface TabButtonProps {
	icon: typeof faHouse;
	label: string;
	active: boolean;
	onClick: () => void;
	/**
	 * Numeric badge — used by Cart for qty and Chat for unread message
	 * count. Outranks `dot` when both are set; cap rendered at "9+" for
	 * counts >= 10 so the badge stays a fixed-width chip.
	 */
	badge?: number;
	/**
	 * Plain "active" indicator — used by Chat to signal an existing
	 * conversation the user can resume even when there are no unread
	 * messages. Smaller and quieter than `badge` (no number, no
	 * background pill) so it doesn't feel like an alert.
	 */
	dot?: boolean;
}

function TabButton({
	icon,
	label,
	active,
	onClick,
	badge,
	dot,
}: TabButtonProps): JSX.Element {
	const badgeLabel = badge != null && badge >= 10 ? '9+' : badge;
	return (
		<button
			type="button"
			onClick={onClick}
			// PD-14703: bar trimmed (min-h 56→48, py-2→1.5, icon xl→lg) — still
			// above the 44px touch minimum, just less dead vertical band.
			className={`flex-1 flex flex-col items-center justify-center min-h-[48px] py-1.5 gap-0.5 relative ${
				active
					? 'text-brand-primary-fg font-semibold'
					: 'text-brand-primary-fg/85 hover:text-brand-primary-fg'
			}`}
			aria-label={label}
			aria-current={active ? 'page' : undefined}
		>
			<div className="relative">
				<FontAwesomeIcon icon={icon} className="text-lg" />
				{badge != null ? (
					<span className="absolute -top-1.5 -right-2.5 bg-portal-cart text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-0.5">
						{badgeLabel}
					</span>
				) : dot === true ? (
					<span
						className="absolute -top-0.5 -right-1 w-2 h-2 rounded-full bg-portal-accent ring-2 ring-brand-primary"
						aria-hidden="true"
					/>
				) : null}
			</div>
			<span className="text-[10px] font-medium leading-none">{label}</span>
		</button>
	);
}
